import { AxiosError, AxiosInstance, AxiosResponse, CancelTokenSource } from 'axios';
import { capitalize } from 'vue';

import { Filter, FilterCollection } from '@/common/models';
import AuditTrail from '@/common/models/audit/AuditTrail';
import QueryParams from '@/common/models/query-params/QueryParams';
import { resetQueries } from '@/org-app/services/tanstack/queryUtils';
import { IQueryKeys } from '@/validator/Validatable';
import i18n from '@/vueI18n';

export interface IPaginationResult<T> {
  pageSize: number;
  pageOffset: number;
  totalCount: number;
  items: T[];
}

type FriendlyError = AxiosError & { userFriendlyMessage: string };

export interface EntityWithID {
  id: number | string;
}

export interface ApiClass {
  toApi();
}

export interface IBodyParams {
  $count?: string[];
  $filter?: string[];
  $select?: string[];
  $expand?: string[];
  $top?: number[];
  $skip?: number[];
  $orderby?: string[];
}

type ConstructorWithFromApi<T> = {
  new (...args): T;
  fromApi(data);
  queryKeys?: IQueryKeys;
};

export type GetAll<T> = (args?: {
  queryParams?: QueryParams;
  cancelTokenSource?: CancelTokenSource;
}) => Promise<IPaginationResult<T>>;

export type GetOne<T> = (args: {
  id: number | string;
  queryParams?: QueryParams;
  cancelTokenSource?: CancelTokenSource;
}) => Promise<T>;

export type API<T> = (args: {
  params?: QueryParams;
  data?: object;
  cancelTokenSource?: CancelTokenSource;
  urlSuffix?: string;
}) => Promise<T>;

export type Post<T extends ApiClass, R extends ApiClass = T> = <Instances extends T | T[]>(args: {
  instances: Instances;
  queryParams: QueryParams;
}) => Promise<Instances extends R[] ? R[] : R>;

export type Put<T extends ApiClass> = (args: { instance: T; queryParams: QueryParams }) => Promise<T>;

export type Delete = (args: { instance: EntityWithID }) => Promise<null>;

export type GetBodyData = (queryParams: QueryParams) => IBodyParams;

class ApiUtils {
  constructor(private axiosInstanceGetter: () => AxiosInstance) {}

  static getError(error): FriendlyError | Error {
    if (typeof error === 'string') {
      return new Error(error);
    }
    let { message } = error;
    if (error == null || error.response == null) {
      message = 'unspecified error';
    } else if (
      error.response.data != null &&
      error.response.data instanceof Object &&
      'name' in error.response.data &&
      error.response.data.name != null &&
      error.response.data.name instanceof Array &&
      error.response.data.name.length > 0
    ) {
      message = error.response.data.name.join('<br>');
    } else if (
      error.response.data !== undefined &&
      error.response.data instanceof Object &&
      'error_code' in error.response.data &&
      typeof error.response.data.error_code === 'string' &&
      error.response.data.error_code != null
    ) {
      const errorKey = `errors.e_${error.response.data.error_code.replace(/-/g, '_')}`;
      const errorText = i18n.global.t(errorKey);
      if (errorText !== undefined && errorText !== errorKey) {
        message = capitalize(errorText);
      } else if ('error' in error.response.data && error.response.data.error != null) {
        message = ` - ${error.response.data.error}`;
      }
    } else if (
      error.response.data instanceof Object &&
      'error' in error.response.data &&
      error.response.data?.error != null &&
      typeof error.response.data.error === 'string'
    ) {
      message = error.response.data?.error.trim().replace('\n', '<br>');
    }
    (error as FriendlyError).userFriendlyMessage = message;
    return error;
  }

  static getResult<T = unknown>(
    successResponse: AxiosResponse,
    entityClass?: ConstructorWithFromApi<T>,
    fromFunction: (x) => NonNullable<T> = null,
  ): IPaginationResult<T> {
    const totalCount = parseInt(successResponse.headers['x-total-count'], 10);
    const items =
      fromFunction || entityClass
        ? successResponse.data.map(fromFunction || entityClass.fromApi)
        : successResponse.data;
    return {
      pageSize: parseInt(successResponse.headers['x-page-size'], 10),
      pageOffset: parseInt(successResponse.headers['x-page-offset'], 10),
      totalCount: Number.isNaN(totalCount) ? successResponse.data.length : totalCount,
      items,
    };
  }

  static getDeliveryResult(
    successResponse: AxiosResponse,
    entityClass,
    fromFunction: (x: unknown) => NonNullable<unknown> = null,
  ) {
    return {
      items: successResponse.data.lineitems.map(fromFunction || entityClass.fromApi),
    };
  }

  createGetAllFunction<T>(
    url: string,
    model: ConstructorWithFromApi<T>,
    queryParamsTransformation: (qp: QueryParams) => QueryParams = null,
    additionalFilters: Filter[] = null,
    resultTransformation: (response: AxiosResponse) => AxiosResponse = null,
    urlTransform: (u: string, params, data?) => string = null,
  ): GetAll<T> {
    return async (
      { queryParams = QueryParams.all(), cancelTokenSource = null } = {
        queryParams: QueryParams.all(),
        cancelTokenSource: null,
      },
    ) => {
      let qp = queryParams;
      if (queryParamsTransformation) {
        qp = queryParamsTransformation(qp);
      }
      let functionUrl = url;
      if (urlTransform) {
        functionUrl = urlTransform(url, qp);
      }
      if (additionalFilters) {
        qp.filterCollection = new FilterCollection([
          ...(qp.filterCollection ? [qp.filterCollection] : []),
          ...additionalFilters,
        ]);
      }
      let cancelToken = null;
      if (cancelTokenSource != null) {
        cancelToken = cancelTokenSource.token;
      } else if (queryParams.cancelTokenSource != null) {
        cancelToken = queryParams.cancelTokenSource.token;
      }
      let response = await this.axiosInstanceGetter()
        .get(functionUrl, {
          params: qp.toObject(),
          cancelToken,
        })
        .catch((err) => {
          throw ApiUtils.getError(err);
        });
      if (response == null) {
        return null;
      }
      if (resultTransformation != null) {
        response = resultTransformation(response);
      }

      return ApiUtils.getResult(response, model);
    };
  }

  createGetOneFunction<T>(
    url: string,
    model: ConstructorWithFromApi<T>,
    queryParamsTransformation?: (qp: QueryParams) => QueryParams,
  ): GetOne<T> {
    return async ({ id, queryParams = QueryParams.all(), cancelTokenSource = null }) => {
      let qp = queryParams;
      if (queryParamsTransformation) {
        qp = queryParamsTransformation(qp);
      }
      if (qp.expand == null && 'expands' in model) {
        qp.expand = model.expands as string[];
      }
      let cancelToken = null;
      if (cancelTokenSource != null) {
        cancelToken = cancelTokenSource.token;
      } else if (queryParams.cancelTokenSource != null) {
        cancelToken = queryParams.cancelTokenSource.token;
      }
      const response = await this.axiosInstanceGetter()
        .get(`${url}${id}`, {
          params: qp.toGetOnePutPostObject(),
          cancelToken,
        })
        .catch((err) => {
          throw ApiUtils.getError(err);
        });
      if (response == null) {
        return null;
      }

      return model.fromApi(response.data);
    };
  }

  createPostFunction<T extends ApiClass, R extends ApiClass = T>(
    url: string,
    model: ConstructorWithFromApi<R>,
    instanceToApiFunction: (x: unknown) => NonNullable<unknown> = null,
    queryParamsTransformation: (qp: QueryParams) => QueryParams = null,
  ): Post<T, R> {
    // Instances can either be one instance or an array of instances. It will return either an array or a single instance depending on the input.
    return async ({ instances, queryParams = QueryParams.all() }) => {
      let qp = queryParams;
      if (queryParamsTransformation) {
        qp = queryParamsTransformation(qp);
      }

      const multiple = Array.isArray(instances);

      let body;

      if (Array.isArray(instances)) {
        body = instances.map((x) => (instanceToApiFunction ? instanceToApiFunction(x) : x.toApi()));
      } else {
        body = instanceToApiFunction ? instanceToApiFunction(instances) : instances.toApi();
      }

      const response = await this.axiosInstanceGetter()
        .post(url, body, { params: qp.toGetOnePutPostObject() })
        .catch((err) => {
          throw ApiUtils.getError(err);
        });

      if (response == null) {
        return null;
      }

      if (model?.queryKeys) {
        // If the model has a cached query of all entities, reset it.
        const { getAll } = model.queryKeys;
        if (getAll) {
          resetQueries([getAll]);
        }
      }

      if (multiple) {
        return response.data.map((x) => model.fromApi(x));
      }
      return model.fromApi(response.data);
    };
  }

  createPatchFunction(url: string) {
    // Instances can either be one instance or an array of instances. It will return either an array or a single instance depending on the input.
    return async ({ data, params = null }) => {
      const response = await this.axiosInstanceGetter()
        .patch(url, data, params != null ? { params } : null)
        .catch((err) => {
          throw ApiUtils.getError(err);
        });

      if (response == null) {
        return null;
      }
      return response.data;
    };
  }

  createPutFunction<T extends ApiClass & EntityWithID>(
    url: string,
    model: ConstructorWithFromApi<T>,
    instanceToApiFunction: (x: unknown) => NonNullable<unknown> = null,
    queryParamsTransformation: (qp: QueryParams) => QueryParams = null,
  ): Put<T> {
    return async ({ instance, queryParams = QueryParams.all() }) => {
      let qp = queryParams;
      if (queryParamsTransformation) {
        qp = queryParamsTransformation(qp);
      }

      const body = instanceToApiFunction
        ? instanceToApiFunction(instance)
        : instance.toApi != null
          ? instance.toApi()
          : instance; // When inline editing in tables we only send the fields that is being modified.

      const response = await this.axiosInstanceGetter()
        .put(`${url}${instance.id}`, body, { params: qp.toGetOnePutPostObject() })
        .catch((err) => {
          throw ApiUtils.getError(err);
        });

      if (response == null) {
        return null;
      }

      if (model?.queryKeys) {
        // If the model has a cached query of all entities, reset it.
        const { getAll } = model.queryKeys;
        if (getAll) {
          resetQueries([getAll]);
        }
      }

      return model.fromApi(response.data);
    };
  }

  createDeleteFunction(url: string): Delete {
    return async ({ instance }) => {
      await this.axiosInstanceGetter()
        .delete(`${url}${instance.id}`)
        .catch((err) => {
          throw ApiUtils.getError(err);
        });

      return null;
    };
  }

  createGetAuditFunction(url: string, modelName: string) {
    return async ({ id }) => {
      const response = await this.axiosInstanceGetter()
        .get(`${url}${id}/audit`)
        .catch((err) => {
          throw ApiUtils.getError(err);
        });
      if (response == null) {
        return null;
      }
      return new AuditTrail(response.data, modelName);
    };
  }

  createApiFunction<T extends ApiClass>(
    url: string,
    method = 'get',
    model: ConstructorWithFromApi<T>,
    returnsList = false,
    paramsTransformation: (qp: QueryParams | unknown) => QueryParams | unknown = null,
    urlTransform: (u: string, params, data) => string = null,
    returnsCount = false,
    dataTransform: (data) => unknown = null,
  ): API<T> {
    return async ({ params = {}, data = {}, cancelTokenSource = null, urlSuffix = null } = {}) => {
      let urlTransformed = url;
      if (urlTransform != null) {
        urlTransformed = urlTransform(url, params, data);
      }
      const paramsTransformed = paramsTransformation != null ? paramsTransformation(params) : params;
      const response = await this.axiosInstanceGetter()
        .request({
          url: urlSuffix != null ? `${urlTransformed}${urlSuffix}` : urlTransformed,
          method,
          params: paramsTransformed instanceof QueryParams ? paramsTransformed.toObject() : paramsTransformed,
          data,
          cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
        })
        .catch((err) => {
          throw ApiUtils.getError(err);
        });
      if (response == null) {
        return null;
      }

      if (model) {
        if (returnsList) {
          return response.data.map(model.fromApi);
        }
        if (returnsCount) {
          return { data: model.fromApi(response.data), totalCount: +response.headers['x-total-count'] };
        }
        return model.fromApi(
          response.data instanceof Array && response.data?.length > 0 ? response.data[0] : response.data,
        );
      }
      if (dataTransform) {
        return dataTransform(response.data);
      }
      return response.data;
    };
  }

  static getBodyData(queryParams: QueryParams) {
    const removeEmptyObjects = (obj) =>
      Object.fromEntries(
        Object.entries(obj).filter(
          ([, value]) => !(typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0),
        ),
      );

    const $filter =
      queryParams.filterCollection && queryParams.filterCollection.expression != ''
        ? [queryParams.filterCollection.expression]
        : null;
    const $select = queryParams.include ?? null;
    const $expand = queryParams.expand ?? null;
    const $top = queryParams.top ? [queryParams.top] : null;
    const $skip = queryParams.skip != null ? [queryParams.skip] : null;
    const $orderby = queryParams.sort ? [queryParams.sort] : null;
    const $count = queryParams.returnsCount != null ? [queryParams.returnsCount.toString()] : null;

    const body: IBodyParams = {};

    // Only send params that are defined
    if ($filter) body.$filter = $filter;
    if ($select) body.$select = $select;
    if ($expand) body.$expand = $expand;
    if ($top) body.$top = $top;
    if ($skip) body.$skip = $skip;
    if ($orderby) body.$orderby = $orderby;
    if ($count) body.$count = $count;

    return removeEmptyObjects(body);
  }
}

export default ApiUtils;
